import { MouseEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../components/HeroSection';
import { ContactForm } from '../components/ContactForm';
import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { DEFAULT_QUICKLINKS } from '../data/defaults';
import { mediaUrl } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import {
  downloadDoc,
  getDocDisplayTitle,
  getDocFileName,
  normalizeDocText,
} from '../utils/documents';
import varlik1 from '../components/assets/icon-recycle.png';
import varlik2 from '../components/assets/Varlık 2.png';
import varlik31 from '../components/assets/Varlık 31.png';
import varlik32 from '../components/assets/Varlık 32.png';
import varlik42 from '../components/assets/icon-calendar-check.png';
import varlik43 from '../components/assets/icon-shield.png';
import varlik93 from '../components/assets/Varlık 93.png';
import varlik44 from '../components/assets/icon-clipboard.png';
import varlik46 from '../components/assets/icon-warning.png';
import varlik48 from '../components/assets/icon-iso-50001.png';
import varlik49 from '../components/assets/icon-iso-45001.png';
import varlik50 from '../components/assets/icon-iso-14001.png';
import varlik51 from '../components/assets/icon-iso-9001.png';
import varlik52 from '../components/assets/icon-documents.png';
import logoColor from '../components/assets/adana_renkli.svg';
import iconCalendar from '../components/assets/icon-calendar.png';
import iconHexagon from '../components/assets/icon-hexagon.png';
import iconArrowLeft from '../components/assets/icon-arrow-left.png';
import iconArrowRight from '../components/assets/icon-arrow-right.png';
import iconPin from '../components/assets/icon-pin.png';
import { DocumentItem } from '../types/content';

const QUICKLINK_ICONS: Record<string, ReactNode> = {
  Konteyner: <img src={varlik32} alt="" />,
  'Genel Kargo / Dökme Yük': <img src={varlik31} alt="" />,
  'Proje Kargo': <img src={varlik2} alt="" />,
  'Antrepo, Depo ve Silo': <img src={varlik93} alt="" />,
  'Atık Alım': <img src={varlik1} alt="" />,
};

// Anasayfa banner altindaki seritte yalnizca bu bes hizmet gosterilir; sira sabittir.
const QUICKLINK_SPECS = [
  { label: 'Konteyner', keys: ['konteyner'] },
  { label: 'Genel Kargo / Dökme Yük', keys: ['dokme', 'genel kargo'] },
  { label: 'Proje Kargo', keys: ['proje'] },
  { label: 'Antrepo, Depo ve Silo', keys: ['antrepo', 'depo', 'silo'] },
  { label: 'Atık Alım', keys: ['atik'] },
];

function simplifyLabel(value: string) {
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g');
}

function resolveQuicklinkIcon(label: string): ReactNode {
  if (QUICKLINK_ICONS[label]) return QUICKLINK_ICONS[label];
  const normalized = simplifyLabel(label);
  if (normalized.includes('konteyner')) return QUICKLINK_ICONS.Konteyner;
  if (normalized.includes('dokme') || normalized.includes('genel kargo')) {
    return QUICKLINK_ICONS['Genel Kargo / Dökme Yük'];
  }
  if (normalized.includes('proje')) return QUICKLINK_ICONS['Proje Kargo'];
  if (normalized.includes('antrepo') || normalized.includes('depo') || normalized.includes('silo')) {
    return QUICKLINK_ICONS['Antrepo, Depo ve Silo'];
  }
  if (normalized.includes('atik')) return QUICKLINK_ICONS['Atık Alım'];
  return QUICKLINK_ICONS.Konteyner;
}

type DocFilter = 'all' | 'tarifeler' | 'sertifikalar' | 'rehber';

function getDocFilter(doc: DocumentItem): DocFilter {
  // Portalda yonetilen kartlarda grup acikca secilir
  if (doc.group) {
    return doc.group === 'tarifeler' || doc.group === 'sertifikalar' || doc.group === 'rehber' ? doc.group : 'all';
  }
  const title = normalizeDocText(doc.title);
  const category = normalizeDocText(doc.category);

  if (category.includes('tarife') || title.includes('tarife')) return 'tarifeler';
  if (category.includes('sertifika')) return 'sertifikalar';
  if (title.includes('iso 9001')) return 'sertifikalar';
  if (title.includes('iso 45001')) return 'sertifikalar';
  if (title.includes('iso 14001')) return 'sertifikalar';
  if (title.includes('iso 50001')) return 'sertifikalar';
  if (
    category.includes('rehber') ||
    category.includes('politika') ||
    title.includes('rehber') ||
    title.includes('politika')
  ) {
    return 'rehber';
  }
  return 'all';
}

// Portal Anasayfa Dokuman Kartlari'ndaki ikon secenekleri (portal ile ayni anahtarlar)
const DOC_ICONS_BY_KEY: Record<string, string> = {
  belge: varlik52,
  pano: varlik44,
  iso9001: varlik51,
  iso14001: varlik50,
  iso45001: varlik49,
  iso50001: varlik48,
  uyari: varlik46,
  kalkan: varlik43,
  takvim: varlik42,
};

function getDocIcon(doc: DocumentItem) {
  if (doc.iconKey && DOC_ICONS_BY_KEY[doc.iconKey]) return DOC_ICONS_BY_KEY[doc.iconKey];
  const text = normalizeDocText(`${doc.title} ${doc.category || ''}`);

  if (text.includes('guncel konteyner')) return varlik52;
  if (text.includes('diger tarife')) return varlik52;
  if (text.includes('entegre yonetim')) return varlik44;
  if (text.includes('bluemed')) return varlik44;
  if (text.includes('iso 9001')) return varlik51;
  if (text.includes('iso 45001')) return varlik49;
  if (text.includes('iso 14001')) return varlik50;
  if (text.includes('iso 50001')) return varlik48;
  if (text.includes('tehlikeli yuk')) return varlik46;
  if (text.includes('dangerous cargo handling')) return varlik46;
  if (text.includes('kvkk')) return varlik43;
  if (text.includes('rezervasyon')) return varlik42;
  return undefined;
}

function getDocMeta(doc: DocumentItem) {
  const category = (doc.category || '').replace(/·/g, '-').replace(/\s+/g, ' ').trim();
  if (!category) return 'PDF - DOKUMAN';

  if (!normalizeDocText(category).includes('pdf')) {
    if (getDocFilter(doc) === 'sertifikalar') return 'PDF - SERTİFİKA';
    if (getDocFilter(doc) === 'tarifeler') return 'PDF - İNDİR';
    if (getDocFilter(doc) === 'rehber') return 'PDF - REHBER';
  }

  return category || 'PDF - DOKUMAN';
}

// Portal birimi (TEU, m², TON) bazen aciklamanin basina yaziyor; deger ile
// birlikte gosterilmesi icin ilk kelime birimse degere tasinir.
function moveUnitIntoValue(value: string, label: string) {
  const words = label.trim().split(/\s+/);
  const unit = words[0] || '';
  if (!value.trim() || !/^(teu|ton|m²|m2|kg|adet)$/i.test(unit)) {
    return { value, label };
  }
  return { value: `${value.trim()} ${unit}`, label: words.slice(1).join(' ') };
}

function renderStatValue(value: string) {
  if (value.endsWith('TEU')) {
    const base = value.replace(/TEU$/, '').trim();
    return (
      <>
        {base} <span className="accent">TEU</span>
      </>
    );
  }

  if (value.endsWith('m²')) {
    const base = value.replace(/m²$/, '').trim();
    return (
      <>
        {base} <span className="accent">m²</span>
      </>
    );
  }

  if (value.includes('/')) {
    const [left, right] = value.split('/');
    return (
      <>
        {left}
        <span className="accent">/{right}</span>
      </>
    );
  }

  if (value.endsWith('m') && value.startsWith('-')) {
    const base = value.slice(0, -1).trim();
    return (
      <>
        {base} <span className="accent">m</span>
      </>
    );
  }

  return value;
}

function blockText(value?: string) {
  return value?.replace(/\s+/g, ' ').trim();
}

function normalizeLookup(value?: string) {
  return (value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatHomeHeroTitle(value?: string) {
  const text = blockText(value);
  if (!text) return 'Ticaret <em>Dürüstlük</em> Üzerine Kuruludur';
  if (text.includes('<')) return text;
  return text.replace(/dürüstlük/iu, '<em>Dürüstlük</em>').replace(/\bhonesty\b/i, '<em>$&</em>');
}

// Anasayfada sayfa basina uc haber; kalanlar sayfalama ile gezilir.
const NEWS_PAGE_SIZE = 3;

// En yeniden eskiye siralar; tarih yoksa kayit sirasina (id) duser.
function sortNewsByDate<T extends { publishedAt?: string; createdAt?: string; id: number }>(
  items: T[],
) {
  const time = (item: T) => {
    const raw = item.publishedAt || item.createdAt;
    const parsed = raw ? new Date(raw).getTime() : Number.NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return [...items].sort((a, b) => time(b) - time(a) || b.id - a.id);
}

// Sayfalama: ilk uc sayfa, aktif sayfanin komsulari ve son sayfa gosterilir.

function buildPageItems(current: number, total: number): Array<number | 'gap'> {
  if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);

  const wanted = [1, 2, 3, current - 1, current, current + 1, total]
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);

  const items: Array<number | 'gap'> = [];
  wanted.forEach((page, index) => {
    if (index > 0) {
      const previous = wanted[index - 1];
      if (page === previous) return;
      if (page - previous > 1) items.push('gap');
    }
    items.push(page);
  });
  return items;
}

// Basliklarda ilk kelime kirmizi vurgu alir (stratejik konum, tarifeler...).
function highlightFirstWord(value: string) {
  const text = value.trim();
  if (!text || text.includes('<')) return text;

  const [first, ...rest] = text.split(/\s+/);
  if (rest.length === 0) return text;
  return `<span>${first}</span> ${rest.join(' ')}`;
}

export function HomePage() {
  const { getPage, getBlock, content, getStats } = useSiteContent();
  const { locale, t } = useLanguage();
  const { settings } = content;
  const page = getPage('anasayfa');
  const heroStats = getStats('hero');
  const homeHeroStats = heroStats.slice(0, 3);
  const homeHeroEyebrow = getBlock('anasayfa', 'home_hero_eyebrow');
  const findHomeBlock = (...patterns: string[]) =>
    content.contentBlocks.find((block) => {
      if (block.pageSlug !== 'anasayfa') return false;
      const haystack = normalizeLookup(`${block.title || ''} ${block.content || ''} ${block.blockKey || ''}`);
      return patterns.some((pattern) => haystack.includes(normalizeLookup(pattern)));
    });
  const docsTitleBlock =
    getBlock('anasayfa', 'home_docs_title') ||
    getBlock('anasayfa', 'home_documents_title') ||
    findHomeBlock('Anasayfa Dokuman - Baslik');
  const docsDescriptionBlock =
    getBlock('anasayfa', 'home_docs_description') ||
    getBlock('anasayfa', 'home_documents_description') ||
    findHomeBlock('Anasayfa Dokuman - Aciklama');
  const docsStripTitleBlock =
    getBlock('anasayfa', 'home_docs_strip_title') ||
    getBlock('anasayfa', 'home_documents_ribbon_title') ||
    findHomeBlock('Anasayfa Dokuman - Ust Serit Baslik');
  const docsStripStat1Value =
    getBlock('anasayfa', 'home_docs_strip_stat_1_value') ||
    getBlock('anasayfa', 'home_documents_ribbon_stat_1_value') ||
    findHomeBlock('Anasayfa Dokuman - Ust Serit Deger 1');
  const docsStripStat1Label =
    getBlock('anasayfa', 'home_docs_strip_stat_1_label') ||
    getBlock('anasayfa', 'home_documents_ribbon_stat_1_label') ||
    findHomeBlock('Anasayfa Dokuman - Ust Serit Aciklama 1');
  const docsStripStat2Value =
    getBlock('anasayfa', 'home_docs_strip_stat_2_value') ||
    getBlock('anasayfa', 'home_documents_ribbon_stat_2_value') ||
    findHomeBlock('Anasayfa Dokuman - Ust Serit Deger 2');
  const docsStripStat2Label =
    getBlock('anasayfa', 'home_docs_strip_stat_2_label') ||
    getBlock('anasayfa', 'home_documents_ribbon_stat_2_label') ||
    findHomeBlock('Anasayfa Dokuman - Ust Serit Aciklama 2');
  const ctaStats = [
    {
      id: 'docs-strip-1',
      value:
        blockText(docsStripStat1Value?.content || docsStripStat1Value?.title) ||
        heroStats.find((stat) => stat.label.toLocaleLowerCase('tr-TR').includes('kapasite'))?.value ||
        '',
      label:
        blockText(docsStripStat1Label?.content || docsStripStat1Label?.title) ||
        heroStats.find((stat) => stat.label.toLocaleLowerCase('tr-TR').includes('kapasite'))?.label ||
        '',
    },
    {
      id: 'docs-strip-2',
      value:
        blockText(docsStripStat2Value?.content || docsStripStat2Value?.title) ||
        heroStats.find((stat) =>
          stat.label.toLocaleLowerCase('tr-TR').includes('liman sahasi') ||
          stat.label.toLocaleLowerCase('tr-TR').includes('liman sahası'),
        )?.value ||
        '',
      label:
        blockText(docsStripStat2Label?.content || docsStripStat2Label?.title) ||
        heroStats.find((stat) =>
          stat.label.toLocaleLowerCase('tr-TR').includes('liman sahasi') ||
          stat.label.toLocaleLowerCase('tr-TR').includes('liman sahası'),
        )?.label ||
        '',
    },
  ].filter((stat) => stat.value && stat.label);
  const altyapi = getStats('altyapi');
  const operationStatsLegacy = getStats('home_operations');
  const operationStatsIntro = getStats('services_operations');
  const operationStats =
    operationStatsIntro.length > 0 ? operationStatsIntro : operationStatsLegacy;

  const operationsEyebrow =
    getBlock('hizmetler', 'operations_intro_eyebrow') || getBlock('anasayfa', 'operations_eyebrow');
  const operationsTitle =
    getBlock('hizmetler', 'operations_intro_title') || getBlock('anasayfa', 'operations_title');
  const operationsDescription =
    getBlock('hizmetler', 'operations_intro_description') ||
    getBlock('anasayfa', 'operations_description');
  const operationsButtonText =
    getBlock('hizmetler', 'operations_intro_cta_text') ||
    getBlock('anasayfa', 'operations_button_text');
  const operationsButtonLink =
    getBlock('hizmetler', 'operations_intro_cta_link') ||
    getBlock('anasayfa', 'operations_button_link');
  const apiServiceLinks = content.services
    .filter((item) => item.isPublished !== false)
    .map((item) => ({
      label: item.title,
      path: `/hizmetler#${item.slug || item.id}`,
    }));
  // Serit sabit bes kategoriden olusur; link adresi varsa servisteki hizmetten alinir.
  const quickLinks = QUICKLINK_SPECS.map((spec, index) => {
    const service = apiServiceLinks.find((item) =>
      // Baslik dile gore degisebildigi icin slug (path) da eslesmeye katilir
      spec.keys.some((key) => simplifyLabel(`${item.label} ${item.path.replace(/-/g, ' ')}`).includes(key)),
    );
    const fallback = settings.quickLinks?.[index] || DEFAULT_QUICKLINKS[index];
    return { label: spec.label, path: service?.path || fallback?.path || '/hizmetler' };
  });
  const docs = content.documents || [];
  const mapAddress = settings.mapAddress || settings.footerAddress || 'Sugözü, 01682 Yumurtalık/Adana';
  const mapCoordinates = '36.8394,35.8981';
  const mapCoordinates2 = '36.84634882579529, 35.88847610492852';
  const encodedMapAddress = encodeURIComponent(mapAddress);
  const googleMapUrl = `https://www.google.com/maps/search/?api=1&query=${mapCoordinates2}`;
  const appleMapUrl = `https://maps.apple.com/?q=${mapCoordinates2}`;
  const yandexMapUrl = `https://yandex.com/maps/?text=${mapCoordinates2}`;
  const satelliteMapEmbedUrl = `https://maps.google.com/maps?q=${mapCoordinates}&t=k&z=11&ie=UTF8&iwloc=&output=embed`;
  const [docFilter, setDocFilter] = useState<DocFilter>('all');
  const [newsCategory, setNewsCategory] = useState('all');
  const [newsPage, setNewsPage] = useState(1);
  const docCounts = {
    all: docs.length,
    tarifeler: docs.filter((doc) => getDocFilter(doc) === 'tarifeler').length,
    sertifikalar: docs.filter((doc) => getDocFilter(doc) === 'sertifikalar').length,
    rehber: docs.filter((doc) => getDocFilter(doc) === 'rehber').length,
  };
  const filteredDocs = docs.filter((doc) => docFilter === 'all' || getDocFilter(doc) === docFilter);
  const sortedNews = sortNewsByDate(content.news);
  const newsCategories = Array.from(
    new Set(sortedNews.map((item) => item.category).filter(Boolean)),
  ) as string[];
  const newsFilters = [
    { key: 'all', label: 'Tümü', count: sortedNews.length },
    ...newsCategories.map((category) => ({
      key: category,
      label: category,
      count: sortedNews.filter((item) => item.category === category).length,
    })),
  ];
  const filteredNews =
    newsCategory === 'all'
      ? sortedNews
      : sortedNews.filter((item) => item.category === newsCategory);
  const newsPageCount = Math.max(1, Math.ceil(filteredNews.length / NEWS_PAGE_SIZE));
  const currentNewsPage = Math.min(newsPage, newsPageCount);
  const homeNews = filteredNews.slice(
    (currentNewsPage - 1) * NEWS_PAGE_SIZE,
    currentNewsPage * NEWS_PAGE_SIZE,
  );
  const newsRangeStart = homeNews.length === 0 ? 0 : (currentNewsPage - 1) * NEWS_PAGE_SIZE + 1;
  const newsRangeEnd = newsRangeStart === 0 ? 0 : newsRangeStart + homeNews.length - 1;
  const newsPageItems = buildPageItems(currentNewsPage, newsPageCount);
  const locationEyebrow =
    getBlock('anasayfa', 'home_location_eyebrow') ||
    getBlock('anasayfa', 'home_strategic_eyebrow');
  const locationTitle =
    getBlock('anasayfa', 'home_location_title') ||
    getBlock('anasayfa', 'home_strategic_title');
  const locationDescription =
    getBlock('anasayfa', 'home_location_description') ||
    getBlock('anasayfa', 'home_strategic_description');
  const locationCoordinatesTitle =
    getBlock('anasayfa', 'home_location_coordinates_title') ||
    getBlock('anasayfa', 'home_strategic_coordinates_title');
  const locationCoordinatesText =
    getBlock('anasayfa', 'home_location_coordinates_text') ||
    getBlock('anasayfa', 'home_strategic_coordinates_text');
  const locationAddressTitle =
    getBlock('anasayfa', 'home_location_address_title') ||
    getBlock('anasayfa', 'home_strategic_address_title');
  const locationAddressText =
    getBlock('anasayfa', 'home_location_address_text') ||
    getBlock('anasayfa', 'home_strategic_address_text');
  const locationDistanceTitle =
    getBlock('anasayfa', 'home_location_distance_title') ||
    getBlock('anasayfa', 'home_strategic_distance_title');
  const locationDistanceText =
    getBlock('anasayfa', 'home_location_distance_text') ||
    getBlock('anasayfa', 'home_strategic_distance_text');
  const locationAccessTitle =
    getBlock('anasayfa', 'home_strategic_transport_title');
  const locationAccessText =
    getBlock('anasayfa', 'home_location_access_text') ||
    getBlock('anasayfa', 'home_strategic_transport_text');
  // Satirlar portalda alt alta (veya "|" ile) yazilir. Blok kayitliysa varsayilan metin
  // satir satir karistirilmaz: tek satir girildiyse ikinci satir bos kalir.
  const blockLines = (block?: { content?: string }) =>
    (block?.content || '')
      .replace(/\\n/g, '\n')
      .split(/\r?\n|\|/)
      .map((item) => item.trim())
      .filter(Boolean);
  const lineOr = (block: { content?: string } | undefined, lines: string[], index: number, fallback: string) =>
    block?.content?.trim() ? lines[index] || '' : fallback;
  const coordinateLines = blockLines(locationCoordinatesText);
  const addressLines = blockLines(locationAddressText);
  const distanceLines = blockLines(locationDistanceText);
  const accessLines = blockLines(locationAccessText);

  return (
    <>
      {/* HeroSection metinleri kendi icinde cevrilir */}
      <HeroSection
        eyebrow={homeHeroEyebrow?.content || settings.slogan || 'Ticaret Dürüstlük Üzerine Kuruludur'}
        title={formatHomeHeroTitle(page?.heroTitle)}
        subtitle={page?.heroSubtitle}
        imageUrl={page?.heroImage}
        stats={homeHeroStats}
        ctaText={page?.heroCtaText || 'Teklif Al'}
        ctaLink={page?.heroCtaLink || '#teklif'}
        variant="home-split"
      />

      {/* Quicklinks */}
      <div className="quicklinks container">
        {quickLinks.map((q) => (
          <Link to={q.path} key={q.label}>
            <span className="quicklink-icon">
              {resolveQuicklinkIcon(q.label)}
            </span>
            <span className="quicklink-label">{t(q.label)}</span>
          </Link>
        ))}
      </div>

      {/* Operasyonlar */}
      <section className="home-operations-section">
        <div className="container home-operations-layout">
          <div className="home-operations-copy">
            <div className="eyebrow-sm">
              {t(operationsEyebrow?.content || operationsEyebrow?.title)}
            </div>
            <h2 className="home-operations-title">
              {t(operationsTitle?.content || operationsTitle?.title)}
            </h2>
            <p className="home-operations-desc">
              {t(operationsDescription?.content)}
            </p>
            <DocumentLinks documents={operationsDescription?.documents} />
            <Link
              to={operationsButtonLink?.content || '/hizmetler'}
              className="btn btn-navy home-operations-button"
            >
              {t(operationsButtonText?.content)}
            </Link>
          </div>
          <div className="home-operations-stats">
            {(operationStats.length > 0 ? operationStats : altyapi).slice(0, 4).map((s, index) => (
              <div
                key={s.id}
                className={`home-stat-card ${index === 0 ? 'primary' : ''}`}
              >
                <b>{renderStatValue(s.value)}</b>
                <span>{t(s.label)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <div className="cta-strip home-cta-strip">
        <div className="container">
          <h3>{t(docsStripTitleBlock?.content || docsStripTitleBlock?.title || '')}</h3>
          <div className="hero-stats">
            {ctaStats.map((s) => {
              const stat = moveUnitIntoValue(s.value, s.label);
              return (
                <div key={s.id}>
                  <b>{renderStatValue(stat.value)}</b>
                  <span>{t(stat.label)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tarifeler & Belgeler */}
      <section id="teklif" className="docs-section">
        <div className="container">
          <h2
            className="docs-title center"
            dangerouslySetInnerHTML={{
              __html: highlightFirstWord(
                t(docsTitleBlock?.content || docsTitleBlock?.title || ''),
              ),
            }}
          />
          <p className="docs-desc center mx-auto">
            {t(docsDescriptionBlock?.content || docsDescriptionBlock?.title || '')}
          </p>
          <div className="docs-filters">
            <button
              type="button"
              className={`docs-filter ${docFilter === 'all' ? 'active' : ''}`}
              onClick={() => setDocFilter('all')}
            >
              {t('Tümü')}
              <span>{docCounts.all}</span>
            </button>
            <button
              type="button"
              className={`docs-filter ${docFilter === 'tarifeler' ? 'active' : ''}`}
              onClick={() => setDocFilter('tarifeler')}
            >
              {t('Tarifeler')}
              <span>{docCounts.tarifeler}</span>
            </button>
            <button
              type="button"
              className={`docs-filter ${docFilter === 'sertifikalar' ? 'active' : ''}`}
              onClick={() => setDocFilter('sertifikalar')}
            >
              {t('Sertifikalar')}
              <span>{docCounts.sertifikalar}</span>
            </button>
            <button
              type="button"
              className={`docs-filter ${docFilter === 'rehber' ? 'active' : ''}`}
              onClick={() => setDocFilter('rehber')}
            >
              {t('Rehber')}
              <span>{docCounts.rehber}</span>
            </button>
          </div>
          <div className="docs-card-grid">
            {filteredDocs.map((d) => {
              const icon = getDocIcon(d);
              const isPrimary = getDocFilter(d) === 'tarifeler';

              return (
                <a
                  className={`docs-card ${isPrimary ? 'primary' : ''}`}
                  href={d.fileUrl || '#'}
                  key={d.id}
                  title={t(getDocDisplayTitle(d))}
                  download={getDocFileName(d)}
                  onClick={(event: MouseEvent<HTMLAnchorElement>) => downloadDoc(event, d)}
                >
                  <div className={`docs-card-icon ${icon ? 'has-image' : ''}`}>
                    {icon ? <img src={icon} alt="" /> : <div className="docs-card-icon-fallback" />}
                  </div>
                  <h3>{t(getDocDisplayTitle(d))}</h3>
                  <small>{t(getDocMeta(d))}</small>
                </a>
              );
            })}
            {filteredDocs.length === 0 && (
              <div className="docs-empty">{t('Bu kategoride belge bulunamadı.')}</div>
            )}
          </div>
        </div>
      </section>

      {/* Konum */}
      <section className="location-section">
        <div className="location-layout">
          <div className="location-copy">
            <div className="location-eyebrow">{t(locationEyebrow?.content)}</div>
            <h2
              className="location-title"
              dangerouslySetInnerHTML={{
                __html: highlightFirstWord(
                  t(locationTitle?.content || "Akdeniz'in Kalbinde İskenderun Körfezi'nde"),
                ),
              }}
            />
            <p className="location-description">
              {t(
                locationDescription?.content ||
                  "TBH Port Adana, İskenderun Körfezi'nin girişinde, Yumurtalık ilçesinde konumlanır. Doğu Akdeniz'in en stratejik noktalarından biri olan limanımız; karayolu, demiryolu ve denizyolu bağlantılarıyla tüm hinterlanda erişim sağlar.",
              )}
            </p>
            <div className="location-card-grid">
              <div className="location-card">
                <strong>{t(locationCoordinatesTitle?.content || 'KOORDİNATLAR')}</strong>
                <b>
                  {t(lineOr(locationCoordinatesText, coordinateLines, 0, "36° 50' 22'' Kuzey"))}
                  {coordinateLines[1] && (
                    <>
                      <br />
                      {t(coordinateLines[1])}
                    </>
                  )}
                </b>
                <span>{t(lineOr(locationCoordinatesText, coordinateLines, 2, 'UN/LOCODE: TRYUM'))}</span>
              </div>
              <div className="location-card">
                <strong>{t(locationAddressTitle?.content || 'ADRES')}</strong>
                <b>
                  {t(lineOr(locationAddressText, addressLines, 0, 'Sugözü, 01682'))}
                  {addressLines[1] && (
                    <>
                      <br />
                      {t(addressLines[1])}
                    </>
                  )}
                </b>
              </div>
              <div className="location-card">
                <strong>{t(locationDistanceTitle?.content || 'BAĞLANTI MESAFELERİ')}</strong>
                <b>{t(lineOr(locationDistanceText, distanceLines, 0, 'Adana 70 km - Mersin 130 km'))}</b>
                <span>{t(lineOr(locationDistanceText, distanceLines, 1, 'İskenderun 45 km - Gaziantep 200 km'))}</span>
              </div>
              <div className="location-card">
                <strong>
                  {t(locationAccessTitle?.content || locationAccessTitle?.title || 'ÇOKLU ULAŞIM')}
                </strong>
                <b>{t(lineOr(locationAccessText, accessLines, 0, 'Karayolu Seçenekleri'))}</b>
                <span>{t(lineOr(locationAccessText, accessLines, 1, 'OSB & Sanayi bölgelerine yakınlık'))}</span>
              </div>
            </div>
          </div>
          <div className="location-map-panel">
            <iframe
              className="location-map-frame"
              src={settings.mapEmbedUrl || satelliteMapEmbedUrl}
              title={t('TBH Port Adana Konum Haritasi')}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="location-map-logo">
              <img src={logoColor} alt="TBH Port Adana" />
            </div>
            <div className="location-map-actions">
              <a href={googleMapUrl} target="_blank" rel="noreferrer" className="location-map-action">
                {t('Google Earth’de Aç')}
              </a>
              <a href={appleMapUrl} target="_blank" rel="noreferrer" className="location-map-action">
                Apple
              </a>
              <a href={yandexMapUrl} target="_blank" rel="noreferrer" className="location-map-action">
                Yandex
              </a>
            </div>
          </div>
        </div>
      </section>

      <Calculator />

      {/* Haberler */}
      <section className="news-showcase-section">
        <div className="container">
          <div className="news-showcase-eyebrow">{t('TÜM HABERLER')}</div>
          <h2
            className="news-showcase-title"
            dangerouslySetInnerHTML={{ __html: t('<span>Bizden</span> Haberler') }}
          />
          <div className="news-showcase-filters">
            {newsFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={newsCategory === filter.key ? 'active' : ''}
                onClick={() => {
                  setNewsCategory(filter.key);
                  setNewsPage(1);
                }}
              >
                {t(filter.label)}
                <span>{filter.count}</span>
              </button>
            ))}
          </div>
          <div className="news-showcase-grid">
            {homeNews.map((n) => (
              <article className="news-showcase-card" key={n.id}>
                <div
                  className="news-showcase-image"
                  style={n.imageUrl ? { backgroundImage: `url(${mediaUrl(n.imageUrl)})` } : undefined}
                />
                <div className="news-showcase-body">
                  <h3>{t(n.title)}</h3>
                  {n.summary && <p>{t(n.summary)}</p>}

                  <Link to={`/haberler/${n.slug}`} className="news-showcase-link">
                    {t('Haberi Oku →')}
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="news-showcase-footer">
            {locale === 'en' ? (
              <p>
                Showing{' '}
                <b>
                  {newsRangeStart}–{newsRangeEnd}
                </b>{' '}
                of <b>{filteredNews.length}</b> news items
              </p>
            ) : (
              <p>
                Toplam <b>{filteredNews.length}</b> haberin{' '}
                <b>
                  {newsRangeStart}–{newsRangeEnd}
                </b>{' '}
                arası gösteriliyor
              </p>
            )}
            <div className="news-showcase-pagination">
              <button
                type="button"
                aria-label={t('Önceki')}
                disabled={currentNewsPage === 1}
                onClick={() => setNewsPage(currentNewsPage - 1)}
              >
                <img src={iconArrowLeft} alt="" />
              </button>
              {newsPageItems.map((item, index) =>
                item === 'gap' ? (
                  <span className="news-showcase-pagination-gap" key={`gap-${index}`}>
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={item === currentNewsPage ? 'active' : ''}
                    onClick={() => setNewsPage(item)}
                  >
                    {item}
                  </button>
                ),
              )}
              <button
                type="button"
                aria-label={t('Sonraki')}
                disabled={currentNewsPage === newsPageCount}
                onClick={() => setNewsPage(currentNewsPage + 1)}
              >
                <img src={iconArrowRight} alt="" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* İletişim */}
      <ContactSection />
    </>
  );
}

function resolveRateValue(
  rates: { rateKey: string; value: string }[],
  rateKey: string,
  fallback: number,
) {
  const found = rates.find((rate) => rate.rateKey === rateKey);
  if (!found) return fallback;
  const parsed = Number(String(found.value).replace(',', '.').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
}

function Calculator() {
  const { content } = useSiteContent();
  const { t, uiLocale } = useLanguage();
  const rates = content.calculatorRates || [];
  const containerOptions = [
    { value: '20', title: "20'", subtitle: 'Standart' },
    { value: '40', title: "40'", subtitle: 'Standart' },
    { value: '45', title: "45'", subtitle: 'High Cube' },
  ];
  const serviceOptions = [
    { value: 'import', label: 'İthalat Dolu' },
    { value: 'transit', label: 'Transit' },
  ];
  const [size, setSize] = useState('40');
  const [serviceType, setServiceType] = useState<'import' | 'transit'>('import');
  const [quantity, setQuantity] = useState(3);
  const [startDate, setStartDate] = useState('2026-08-08');
  const [endDate, setEndDate] = useState('2026-08-22');
  const start = new Date(startDate);
  const end = new Date(endDate);
  const rawDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const days = Number.isFinite(rawDays) ? Math.max(1, rawDays) : 1;
  const dayLabel = `${days} ${t('Gün')}`;
  const rateMultiplier = resolveRateValue(
    rates,
    size === '20' ? 'container_20' : size === '40' ? 'container_40' : 'container_45',
    size === '20' ? 34.5 : size === '40' ? 50.05 : 57.25,
  );
  const serviceMultiplier = resolveRateValue(
    rates,
    serviceType === 'import' ? 'service_import' : 'service_transit',
    serviceType === 'import' ? 1 : 0.82,
  );
  const effectiveRate = rateMultiplier * serviceMultiplier;
  const tier1End = Math.max(1, Math.round(resolveRateValue(rates, 'tier_1_end_day', 5)));
  const tier2End = Math.max(tier1End + 1, Math.round(resolveRateValue(rates, 'tier_2_end_day', 11)));
  const tier3End = Math.max(tier2End + 1, Math.round(resolveRateValue(rates, 'tier_3_end_day', 20)));
  const dayWord = t('gün');
  const tierRanges = [
    {
      label: `0 - ${tier1End} ${dayWord}`,
      days: Math.min(days, tier1End),
      multiplier: resolveRateValue(rates, 'tier_1_multiplier', 1),
    },
    {
      label: `${tier1End} - ${tier2End} ${dayWord}`,
      days: Math.min(Math.max(days - tier1End, 0), tier2End - tier1End),
      multiplier: resolveRateValue(rates, 'tier_2_multiplier', 1.28),
    },
    {
      label: `${tier2End} - ${tier3End} ${dayWord}`,
      days: Math.min(Math.max(days - tier2End, 0), tier3End - tier2End),
      multiplier: resolveRateValue(rates, 'tier_3_multiplier', 1.9),
    },
    {
      label: `${tier3End}+ ${dayWord}`,
      days: Math.max(days - tier3End, 0),
      multiplier: resolveRateValue(rates, 'tier_4_multiplier', 2.35),
    },
  ].filter((tier) => tier.days > 0);
  const tierItems = tierRanges.map((tier) => {
    const subtotal = tier.days * effectiveRate * quantity * tier.multiplier;
    return {
      ...tier,
      subtotal,
      detail: `${effectiveRate.toLocaleString(uiLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} USD x ${tier.days} x ${quantity} ${t('Konteyner')}`,
    };
  });
  const total = tierItems.reduce((sum, tier) => sum + tier.subtotal, 0);
  const dominantTier = Math.min(
    3,
    Math.max(
      0,
      tierItems.findIndex((tier) => tier.multiplier >= resolveRateValue(rates, 'tier_3_multiplier', 1.9)),
    ),
  );
  const selectedContainer = containerOptions.find((option) => option.value === size) || containerOptions[1];
  const selectedService = serviceOptions.find((option) => option.value === serviceType) || serviceOptions[0];
  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(uiLocale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

  return (
    <section className="calculator-section">
      <div className="container">
        <div className="calculator-shell">
          <div className="calculator-form-panel">
            <div className="calculator-eyebrow">{t('TAHMİNİ HESAPLAMA')}</div>
            <h2 className="calculator-title">{t('Ardiye Hesaplayıcı')}</h2>
            <p className="calculator-description">
              {t(
                'Girdiğiniz bilgiler kaydedilmez. Sonuç tahmin niteliğindedir; fatura için resmi tarife esas alınır.',
              )}
            </p>

            <div className="calculator-group">
              <label className="calculator-label">{t('Konteyner Tipi')}</label>
              <div className="calculator-size-grid">
                {containerOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`calculator-size-card ${size === option.value ? 'active' : ''}`}
                    onClick={() => setSize(option.value)}
                  >
                    <span className="calculator-size-title">{option.title}</span>
                    <span className="calculator-size-subtitle">{t(option.subtitle)}</span>
                    {size === option.value && <span className="calculator-size-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="calculator-group">
              <label className="calculator-label">
                {t('Liman Giriş ve Tahmini Çıkış Tarihi')}
              </label>
              <div className="calculator-date-grid">
                <label className="calculator-date-field">
                  <span className="calculator-date-icon">
                    <img src={iconCalendar} alt="" />
                  </span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <em>{formatDate(startDate)}</em>
                </label>
                <label className="calculator-date-field">
                  <span className="calculator-date-icon">
                    <img src={iconCalendar} alt="" />
                  </span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  <em>{formatDate(endDate)}</em>
                </label>
              </div>
            </div>

            <div className="calculator-group">
              <label className="calculator-label">{t('Hizmet Türü')}</label>
              <div className="calculator-service-toggle">
                {serviceOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={serviceType === option.value ? 'active' : ''}
                    onClick={() => setServiceType(option.value as 'import' | 'transit')}
                  >
                    {t(option.label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="calculator-group">
              <label className="calculator-label">{t('Konteyner Adedi')}</label>
              <label className="calculator-quantity-field">
                <span className="calculator-quantity-icon">
                  <img src={iconHexagon} alt="" />
                </span>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                />
                <em>
                  {quantity} {t('Adet')}
                </em>
              </label>
            </div>

            <button type="button" className="calculator-submit">
              {t('Ücreti Hesapla')} <span>→</span>
            </button>
          </div>

          <div className="calculator-result-panel">
            <div className="calculator-result-eyebrow">{t('TAHMINI TOPLAM ÜCRET')}</div>
            <div className="calculator-result-meta">
              {quantity} x {selectedContainer.title} {t(selectedService.label)} - {dayLabel}
            </div>
            <div className="calculator-total-row">
              <strong>{total.toLocaleString(uiLocale, { maximumFractionDigits: 1 })}</strong>
              <span>USD</span>
            </div>
            <div className="calculator-breakdown">
              {tierItems.map((tier) => (
                <div className="calculator-breakdown-item" key={tier.label}>
                  <div>
                    <b>
                      {tier.label} - {tier.days} {dayWord}
                    </b>
                    <small>{tier.detail}</small>
                  </div>
                  <strong>
                    {tier.subtotal.toLocaleString(uiLocale, { maximumFractionDigits: 1 })} USD
                  </strong>
                </div>
              ))}
            </div>
            <div className="calculator-tier-bar">
              {[0, 1, 2, 3].map((index) => (
                <span
                  key={index}
                  className={
                    index < dominantTier ? 'done' : index === dominantTier ? 'active' : 'idle'
                  }
                />
              ))}
            </div>
            <div className="calculator-tier-labels">
              <span>{t('1.Dilim')}</span>
              <span>{t('2.Dilim')}</span>
              <span>{t('3.Dilim')}</span>
              <span>{t('4.Dilim')}</span>
            </div>
            <div className="calculator-actions">
              <a href="#teklif" className="calculator-action-outline">
                {t('PDF İndir')}
              </a>
              <Link to="/iletisim" className="calculator-action-solid">
                {t('Teklif Al')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ContactSection() {
  const { content, getBlock } = useSiteContent();
  const { t } = useLanguage();
  const { settings } = content;
  const strategicAddressText =
    getBlock('anasayfa', 'home_strategic_address_text') ||
    getBlock('anasayfa', 'home_location_address_text');
  const contactAddress =
    strategicAddressText?.content || strategicAddressText?.title || settings.footerAddress || 'Sugözü, 01682 Yumurtalık/Adana';
  return (
    <section className="contact-showcase-section">
      <div className="container contact-showcase-layout">
        <div className="contact-showcase-copy">
          <div className="contact-showcase-eyebrow">{t('İLETİŞİM')}</div>
          <h2 className="contact-showcase-title">{t('Bizimle İletişime Geçin')}</h2>
          <div className="contact-showcase-list">
            <div>
              <strong>
                <img src={iconPin} alt="" className="contact-showcase-inline-icon" />
                {t('ADRES')}
              </strong>
              <span>{t(contactAddress)}</span>
            </div>
            <div>
              <strong>{t('TELEFON')}</strong>
              <span>{settings.footerPhone || '+90 322 000 00 00'}</span>
            </div>
            <div>
              <strong>{t('E-POSTA')}</strong>
              <span>{settings.footerEmail || 'info@tbhport.com'}</span>
            </div>
          </div>
          <a
            href="https://kariyer.sanshine.com.tr/Liman/"
            className="contact-showcase-career"
            target="_blank"
            rel="noreferrer"
          >
            {t('Kariyer Sayfası →')}
          </a>
        </div>
        <ContactForm />
      </div>
    </section>
  );
}
